// Page "Pièces jointes" : liste (mail + import local), sélection, envoi à l'OCR/extraction, et le
// chargement initial des métadonnées depuis le fil de discussion (loadAttachments, appelé par
// displayMailContent dans outlook.ts). resetAttachmentsPanel() est le point d'entrée utilisé par
// resetReplyPanel() (outlook.ts) pour repartir d'un état propre sans connaître le détail interne.
import { bindActivate, describeActionError } from "../ui/domHelpers";
import { getMessageAttachments } from "../graph";
import { MailAttachment, mailData } from "../mailData";
import { extractAttachmentsText } from "../praetoria";

let isExtractingAttachments = false;
let attachmentsLoadId = 0;
let attachmentsLoading = false;

export function setAttachmentsProcessingNotice(inProgress: boolean): void {
  document.getElementById("attachments-processing-notice").classList.toggle("is-hidden", !inProgress);
}

function updateAttachmentsUsedBadge(): void {
  const badgeElement = document.getElementById("attachments-used-badge");
  const usedCount = mailData.attachments.filter((attachment) => attachment.scanned && attachment.included).length;
  badgeElement.textContent = String(usedCount);
  badgeElement.classList.toggle("is-hidden", usedCount === 0);
}

function attachmentsSelectableCheckboxes(): HTMLInputElement[] {
  return Array.from(document.querySelectorAll<HTMLInputElement>("#attachments-list .attachment-list-checkbox"));
}

function updateSelectAllAttachmentsButton(): void {
  const buttonElement = document.getElementById("attachments-select-all");
  const labelElement = document.getElementById("attachments-select-all-label");
  const checkboxes = attachmentsSelectableCheckboxes();

  if (checkboxes.length === 0) {
    buttonElement.classList.add("is-hidden");
    return;
  }

  buttonElement.classList.remove("is-hidden");
  const allChecked = checkboxes.every((checkboxElement) => checkboxElement.checked);
  labelElement.textContent = allChecked ? "Tout désélectionner" : "Tout sélectionner";
}

function handleToggleSelectAllAttachments(): void {
  const checkboxes = attachmentsSelectableCheckboxes();
  if (checkboxes.length === 0) {
    return;
  }

  const nextChecked = !checkboxes.every((checkboxElement) => checkboxElement.checked);

  checkboxes.forEach((checkboxElement) => {
    if (checkboxElement.checked === nextChecked) {
      return;
    }
    checkboxElement.checked = nextChecked;

    const index = Number(checkboxElement.id.replace("attachment-ocr-", ""));
    const attachment = mailData.attachments[index];
    if (attachment?.scanned) {
      attachment.included = nextChecked;
      checkboxElement.setAttribute(
        "aria-label",
        nextChecked
          ? `Ne plus utiliser "${attachment.filename}" pour générer la réponse`
          : `Utiliser "${attachment.filename}" pour générer la réponse`
      );
      checkboxElement
        .closest(".attachment-list-item")
        ?.classList.toggle("attachment-list-item--excluded", !nextChecked);
    }
  });

  updateAttachmentsUsedBadge();
  updateSelectAllAttachmentsButton();
}

function renderAttachmentsList(): void {
  updateAttachmentsUsedBadge();

  const listElement = document.getElementById("attachments-list");
  listElement.innerHTML = "";

  if (mailData.attachments.length === 0) {
    const emptyElement = document.createElement("p");
    emptyElement.className = "attachment-list-empty";
    emptyElement.textContent = attachmentsLoading
      ? "Recherche des pièces jointes en cours..."
      : "Aucune pièce jointe détectée dans cette conversation.";
    listElement.appendChild(emptyElement);
    updateSelectAllAttachmentsButton();
    return;
  }

  mailData.attachments.forEach((attachment, index) => {
    const isConfirmedUnreadable = !!attachment.erreur;
    // Tant qu'une pj n'a pas été envoyée en lecture (via "Valider"), on ne peut que deviner
    // sa lisibilité d'après son type de fichier -- ce n'est pas une vérification serveur.
    const isLikelyUnsupported = !attachment.scanned && !attachment.supported;
    const isUnreadable = isConfirmedUnreadable || isLikelyUnsupported;

    const itemElement = document.createElement("div");
    itemElement.className = "attachment-list-item";
    itemElement.classList.toggle("attachment-list-item--unreadable", isUnreadable);
    itemElement.classList.toggle("attachment-list-item--excluded", attachment.scanned && !attachment.included);

    const nameWrapElement = document.createElement("span");
    nameWrapElement.className = "attachment-list-name-wrap";

    const sourceIconElement = document.createElement("span");
    sourceIconElement.className = "attachment-list-source";
    sourceIconElement.setAttribute("aria-hidden", "true");
    sourceIconElement.innerHTML = attachment.source === "local" ? SOURCE_ICON_LOCAL : SOURCE_ICON_MAIL;
    sourceIconElement.title = attachment.source === "local" ? "Importée depuis votre ordinateur" : "Pièce jointe du mail";

    const nameElement = document.createElement("span");
    nameElement.className = "attachment-list-name";
    nameElement.textContent = attachment.filename;
    nameElement.title = attachment.filename;

    nameWrapElement.appendChild(sourceIconElement);
    nameWrapElement.appendChild(nameElement);
    itemElement.appendChild(nameWrapElement);

    if (isUnreadable) {
      const unreadableElement = document.createElement("span");
      unreadableElement.className = "attachment-list-unreadable";
      unreadableElement.textContent = isConfirmedUnreadable ? "Non lisible" : "Format non pris en charge";
      unreadableElement.title = isConfirmedUnreadable
        ? attachment.erreur
        : "Ce type de fichier n'est probablement pas exploitable.";
      itemElement.appendChild(unreadableElement);
    } else {
      const actionsElement = document.createElement("div");
      actionsElement.className = "attachment-list-actions";

      const checkboxElement = document.createElement("input");
      checkboxElement.type = "checkbox";
      checkboxElement.className = "attachment-list-checkbox";
      checkboxElement.id = `attachment-ocr-${index}`;

      if (attachment.scanned) {
        checkboxElement.checked = attachment.included;
        checkboxElement.disabled = false;
        checkboxElement.setAttribute(
          "aria-label",
          attachment.included
            ? `Ne plus utiliser "${attachment.filename}" pour générer la réponse`
            : `Utiliser "${attachment.filename}" pour générer la réponse`
        );
        checkboxElement.addEventListener("change", () => {
          attachment.included = checkboxElement.checked;
          itemElement.classList.toggle("attachment-list-item--excluded", !attachment.included);
          checkboxElement.setAttribute(
            "aria-label",
            attachment.included
              ? `Ne plus utiliser "${attachment.filename}" pour générer la réponse`
              : `Utiliser "${attachment.filename}" pour générer la réponse`
          );
          updateAttachmentsUsedBadge();
          updateSelectAllAttachmentsButton();
        });
      } else {
        checkboxElement.checked = false;
        checkboxElement.disabled = false;
        checkboxElement.setAttribute("aria-label", `Envoyer "${attachment.filename}" à l'OCR`);
      }

      const scanStatusElement = document.createElement("span");
      scanStatusElement.className = "attachment-scan-status";
      setAttachmentScanStatus(scanStatusElement, attachment.scanned);

      actionsElement.appendChild(checkboxElement);
      actionsElement.appendChild(scanStatusElement);
      itemElement.appendChild(actionsElement);
    }

    listElement.appendChild(itemElement);
  });

  updateSelectAllAttachmentsButton();
}

const SOURCE_ICON_MAIL =
  '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>' +
  "</svg>";

const SOURCE_ICON_LOCAL =
  '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>' +
  '<line x1="8" y1="21" x2="16" y2="21"></line>' +
  '<line x1="12" y1="17" x2="12" y2="21"></line>' +
  "</svg>";

const SCAN_STATUS_ICON_PENDING =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M3 7V5a2 2 0 0 1 2-2h2"></path>' +
  '<path d="M17 3h2a2 2 0 0 1 2 2v2"></path>' +
  '<path d="M21 17v2a2 2 0 0 1-2 2h-2"></path>' +
  '<path d="M7 21H5a2 2 0 0 1-2-2v-2"></path>' +
  "</svg>";

const SCAN_STATUS_ICON_DONE =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polyline points="20 6 9 17 4 12"></polyline>' +
  "</svg>";

function setAttachmentScanStatus(scanStatusElement: HTMLElement, scanned: boolean): void {
  scanStatusElement.innerHTML = scanned ? SCAN_STATUS_ICON_DONE : SCAN_STATUS_ICON_PENDING;
  scanStatusElement.dataset.scanned = String(scanned);
  scanStatusElement.title = scanned ? "Scanné" : "Non scanné";
  scanStatusElement.setAttribute("aria-label", scanned ? "Scanné" : "Non scanné");
}

async function handleValidateAttachments(): Promise<void> {
  if (isExtractingAttachments) {
    hideAttachmentsPage();
    return;
  }

  const statusElement = document.getElementById("attachments-page-status");
  const validateButton = document.getElementById("attachments-page-validate");
  const listElement = document.getElementById("attachments-list");

  const selectedIndexes: number[] = [];
  listElement.querySelectorAll<HTMLInputElement>(".attachment-list-checkbox").forEach((checkboxElement) => {
    if (checkboxElement.checked) {
      selectedIndexes.push(Number(checkboxElement.id.replace("attachment-ocr-", "")));
    }
  });

  if (selectedIndexes.length === 0) {
    hideAttachmentsPage();
    return;
  }

  if (!mailData.graphToken) {
    statusElement.textContent = "Session Microsoft expirée, rechargez le module.";
    statusElement.className = "reply-status reply-status--error";
    return;
  }

  validateButton.classList.add("is-disabled");
  validateButton.setAttribute("aria-disabled", "true");
  statusElement.textContent = `Lecture de ${
    selectedIndexes.length === 1 ? "1 pièce jointe" : `${selectedIndexes.length} pièces jointes`
  } en cours...`;
  statusElement.className = "reply-status";

  // Capturer les objets pièce jointe eux-mêmes (pas juste leur index) : si le mail affiché change
  // pendant l'extraction, mailData.attachments est remplacé par un nouveau tableau (loadAttachments)
  // et un index resterait valide mais pointerait sur une pièce jointe totalement différente.
  const targets = selectedIndexes.map((index) => mailData.attachments[index]);
  const metas = targets.map((attachment) => ({
    name: attachment.filename,
    contentType: attachment.contentType,
    contentBytes: attachment.contentBytes,
  }));
  const loadIdAtStart = attachmentsLoadId;

  isExtractingAttachments = true;
  setAttachmentsProcessingNotice(true);
  hideAttachmentsPage();
  try {
    const extracted = await extractAttachmentsText(metas, mailData.graphToken, ({ processed, total }) => {
      statusElement.textContent = `Lecture de ${processed}/${total} pièce(s) jointe(s) en cours...`;
    });

    if (loadIdAtStart !== attachmentsLoadId) {
      statusElement.textContent = "Le mail affiché a changé pendant la lecture des pièces jointes, résultat ignoré.";
      statusElement.className = "reply-status reply-status--error";
      return;
    }

    extracted.forEach((result, position) => {
      const attachment = targets[position];
      attachment.text = result.text;
      attachment.erreur = result.erreur;
      attachment.scanned = !result.erreur;
    });
    renderAttachmentsList();

    const failed = extracted.filter((result) => result.erreur).length;
    const ok = extracted.length - failed;
    if (ok === 0) {
      statusElement.textContent = `Impossible de lire ${
        extracted.length === 1 ? "la pièce jointe sélectionnée" : "les pièces jointes sélectionnées"
      }.`;
      statusElement.className = "reply-status reply-status--error";
    } else if (failed > 0) {
      statusElement.textContent = `Lecture terminée : ${ok} pièce(s) jointe(s) prise(s) en compte (${failed} non exploitable(s)).`;
      statusElement.className = "reply-status";
    } else {
      statusElement.textContent = `Lecture terminée : ${
        ok === 1 ? "1 pièce jointe prise" : `${ok} pièces jointes prises`
      } en compte.`;
      statusElement.className = "reply-status";
    }
  } catch (error) {
    console.error("Impossible d'extraire le texte des pièces jointes sélectionnées", error);
    statusElement.textContent = describeActionError("Impossible de lire les pièces jointes sélectionnées", error);
    statusElement.className = "reply-status reply-status--error";
  } finally {
    isExtractingAttachments = false;
    setAttachmentsProcessingNotice(false);
    validateButton.classList.remove("is-disabled");
    validateButton.removeAttribute("aria-disabled");
  }
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function handleImportLocalAttachments(fileList: FileList | null): Promise<void> {
  if (!fileList || fileList.length === 0) {
    return;
  }
  for (const file of Array.from(fileList)) {
    const contentBytes = await readFileAsBase64(file);
    const attachment: MailAttachment = {
      filename: file.name,
      contentType: file.type || "application/octet-stream",
      contentBytes,
      supported: isLikelySupportedAttachment(file.name, file.type || ""),
      scanned: false,
      included: true,
      source: "local",
    };
    mailData.attachments.push(attachment);
  }
  renderAttachmentsList();
}

export function showAttachmentsPage(): void {
  document.getElementById("app-body").style.display = "none";
  document.getElementById("attachments-page").style.display = "flex";
  bindActivate("attachments-page-cancel", hideAttachmentsPage);
  bindActivate("attachments-page-validate", () => {
    void handleValidateAttachments();
  });
  bindActivate("attachments-import-local", () => {
    (document.getElementById("attachments-import-input") as HTMLInputElement).click();
  });
  bindActivate("attachments-select-all", handleToggleSelectAllAttachments);

  const importInput = document.getElementById("attachments-import-input") as HTMLInputElement;
  importInput.onchange = () => {
    void handleImportLocalAttachments(importInput.files);
    importInput.value = "";
  };

  const statusElement = document.getElementById("attachments-page-status");
  statusElement.textContent = "";
  statusElement.className = "reply-status";

  renderAttachmentsList();
}

function hideAttachmentsPage(): void {
  document.getElementById("attachments-page").style.display = "none";
  document.getElementById("app-body").style.display = "flex";
}

function setAttachmentsStatus(text: string, options: { loading?: boolean; isError?: boolean } = {}): void {
  const containerElement = document.getElementById("attachments-status");
  const spinnerElement = document.getElementById("attachments-status-spinner");
  const textElement = document.getElementById("attachments-status-text");
  if (!text) {
    containerElement.classList.add("is-hidden");
    return;
  }
  containerElement.classList.remove("is-hidden");
  containerElement.classList.toggle("reply-status--error", !!options.isError);
  spinnerElement.classList.toggle("is-hidden", !options.loading);
  textElement.textContent = text;
}

function dedupeAttachmentMetas<T extends { contentBytes: string }>(metas: T[]): T[] {
  const seen = new Set<string>();
  return metas.filter((meta) => {
    if (!meta.contentBytes) {
      return true;
    }
    if (seen.has(meta.contentBytes)) {
      return false;
    }
    seen.add(meta.contentBytes);
    return true;
  });
}

// Formats qu'un service d'extraction/OCR classique sait généralement traiter. C'est une simple
// estimation côté client (basée sur l'extension/le type MIME) pour prévenir l'utilisateur avant
// tout envoi -- aucun fichier n'est transmis au serveur pour vérifier sa lisibilité réelle.
const LIKELY_SUPPORTED_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "odt",
  "rtf",
  "txt",
  "xls",
  "xlsx",
  "csv",
  "ppt",
  "pptx",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "bmp",
  "tif",
  "tiff",
  "heic",
  "msg",
  "eml",
]);

function isLikelySupportedAttachment(filename: string, contentType: string): boolean {
  const match = /\.([a-z0-9]+)$/i.exec(filename);
  const extension = match ? match[1].toLowerCase() : "";
  if (LIKELY_SUPPORTED_EXTENSIONS.has(extension)) {
    return true;
  }
  return contentType.startsWith("image/") || contentType === "application/pdf";
}

// Appelé par displayMailContent (outlook.ts) une fois le fil de discussion chargé.
export async function loadAttachments(): Promise<void> {
  const loadId = ++attachmentsLoadId;
  const isStale = () => loadId !== attachmentsLoadId;
  const graphToken = mailData.graphToken;
  if (!graphToken || mailData.thread.length === 0) {
    return;
  }

  attachmentsLoading = true;
  renderAttachmentsList();

  let metas: { name: string; contentType: string; contentBytes: string }[] = [];
  let failedMessageCount = 0;
  try {
    const concurrency = 4;
    const perMessage: { name: string; contentType: string; contentBytes: string }[][] = [];
    for (let i = 0; i < mailData.thread.length; i += concurrency) {
      const batch = mailData.thread.slice(i, i + concurrency);
      // allSettled plutôt que all : un message en erreur (supprimé, déplacé, erreur Graph
      // transitoire) ne doit pas faire perdre les pièces jointes des autres messages du fil.
      const results = await Promise.allSettled(batch.map((message) => getMessageAttachments(message.id, graphToken)));
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          perMessage.push(result.value);
        } else {
          failedMessageCount++;
          console.error(`Impossible de lister les pièces jointes du message ${batch[index].id}`, result.reason);
        }
      });
    }
    const rawMetas = perMessage.flat();
    metas = dedupeAttachmentMetas(rawMetas);
    if (metas.length !== rawMetas.length) {
      console.log(
        `[loadAttachments] ${rawMetas.length - metas.length} doublon(s) de pièce(s) jointe(s) ignoré(s) (même contenu répété dans le fil)`
      );
    }
  } catch (error) {
    console.error("Impossible de lister les pièces jointes du thread", error);
    attachmentsLoading = false;
    renderAttachmentsList();
    return;
  }

  if (isStale()) {
    return;
  }
  console.log(`[loadAttachments] ${metas.length} pièce(s) jointe(s) non-inline détectée(s) dans le thread`);

  mailData.attachments = metas.map(
    (meta): MailAttachment => ({
      filename: meta.name,
      contentType: meta.contentType,
      contentBytes: meta.contentBytes,
      supported: isLikelySupportedAttachment(meta.name, meta.contentType),
      scanned: false,
      included: true,
      source: "mail",
    })
  );
  attachmentsLoading = false;
  renderAttachmentsList();
  const detectedText =
    metas.length > 0 ? `${metas.length === 1 ? "1 pièce jointe détectée" : `${metas.length} pièces jointes détectées`}.` : "";
  const failedText =
    failedMessageCount > 0
      ? ` (${failedMessageCount} message${failedMessageCount > 1 ? "s" : ""} du fil non consulté${failedMessageCount > 1 ? "s" : ""}, pièces jointes potentiellement incomplètes.)`
      : "";
  setAttachmentsStatus(detectedText || failedText ? `${detectedText}${failedText}` : "");
}

// Utilisé par resetReplyPanel() (outlook.ts) pour repartir d'une liste vide sans connaître le
// détail interne (attachmentsLoading, renderAttachmentsList, etc.).
export function resetAttachmentsPanel(): void {
  mailData.attachments = [];
  attachmentsLoading = false;
  renderAttachmentsList();
  setAttachmentsStatus("");
}
